export interface MaxFileSizeProps {
  size: number
  readonly toString: string
  isValidFileSize(file: File): boolean
  getFilesExceededSizeLimit(files: File[]): string[]
}

/**
 * An object representing the maximum allowed file size.
 *
 * @example
 * console.log(maxFileSize.maxSizeInMB); // Outputs: 20000000
 * console.log(maxFileSize.toString); // Outputs: "20MB"
 */
export const maxFileSize: MaxFileSizeProps = {
  size: 20e6,
  get toString() {
    return `${this.size / 1e6}MB`
  },
  isValidFileSize(file) {
    return file.size <= this.size
  },
  getFilesExceededSizeLimit(files: File[]): string[] {
    return getFilesExceededSizeLimit(files, this.size)
  },
}

/**
 * Represents a file with a name and size.
 */
export interface File {
  name: string
  size: number
}

/**
 * Simplified version of getFilesExceededSizeLimit to be imported performantly and easily.
 * Returns the names of files that exceed the size limit.
 *
 * @param files - An array of File objects to check.
 * @returns An array of file names that exceeded the size limit.
 */
export const getFilesExceededSizeLimit = (
  files: File[],
  maxSize: number = maxFileSize.size,
): string[] => files.filter(file => file.size > maxSize).map(({ name }) => name)
